import type { ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog/Dialog';
import { Toggle } from '@/shared/components/Toggle/Toggle';
import { NavButton } from '@/shared/components/NavButton/NavButton';
import { PillButton } from '@/shared/components/PillButton/PillButton';
import ComingSoon from '@/shared/components/ComingSoon/ComingSoon';
import { useSettingsStore, type Language } from '@/shared/stores/useSettingsStore';
import { usePWAInstall } from '@/shared/hooks/usePWAInstall';
import { DialogHeading } from './DialogShared';
import { createTextStroke } from '@/styles/textStroke';

// Module-level constants - created once, never recreated
const LANGUAGES: readonly Language[] = ['fr', 'en', 'mg'] as const;

// Language names stay in their native form (not translated)
const LANGUAGE_LABELS: Record<Language, string> = {
  fr: 'FRANCAIS',
  en: 'ENGLISH',
  mg: 'MALAGASY'
};

export interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRulesClick: () => void;
  onFaqClick: () => void;
  onDisconnect: () => void;
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="mb-6">
    <DialogHeading>{title}</DialogHeading>
    <div className="mt-3">{children}</div>
  </div>
);

export function SettingsDialog({
  isOpen,
  onClose,
  onRulesClick: _onRulesClick, // Reserved for future use
  onFaqClick: _onFaqClick, // Reserved for future use
  onDisconnect
}: SettingsDialogProps) {
  const { t } = useTranslation();

  // Zustand store
  const {
    musicEnabled,
    soundEffectsEnabled,
    language,
    notificationsEnabled,
    setMusicEnabled,
    setSoundEffectsEnabled,
    setLanguage,
    setNotificationsEnabled
  } = useSettingsStore();

  // Local dropdown state - component-scoped, not page-level
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Coming soon tooltip state
  const [showRulesComingSoon, setShowRulesComingSoon] = useState(false);
  const [showFaqComingSoon, setShowFaqComingSoon] = useState(false);

  // PWA install
  const { isInstalled, isInstallable, openDialog } = usePWAInstall();

  // Auto-close dropdown and tooltips when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsDropdownOpen(false);
      setShowRulesComingSoon(false);
      setShowFaqComingSoon(false);
    }
  }, [isOpen]);

  // Click-outside-to-close behavior
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Keyboard navigation for dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  const handleLanguageSelect = (lang: Language) => {
    setLanguage(lang);
    setIsDropdownOpen(false);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      variant="grey"
      title={t('settings.title')}
      showCloseButton={true}
      className="max-w-md"
    >
      <div className="p-6 space-y-6">
        {/* AUDIO Section */}
        <Section title={t('settings.audio')}>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <span
                className="text-white font-bold text-sm font-mind-boggle"
                style={createTextStroke('1.5px')}
              >
                {t('settings.music')}
              </span>
              <Toggle enabled={musicEnabled} onChange={setMusicEnabled} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span
                className="text-white font-bold text-sm font-mind-boggle"
                style={createTextStroke('1.5px')}
              >
                {t('settings.soundEffects')}
              </span>
              <Toggle enabled={soundEffectsEnabled} onChange={setSoundEffectsEnabled} />
            </div>
          </div>
        </Section>

        {/* LANGUES Section */}
        <Section title={t('settings.languages')}>
          <div className="relative" ref={dropdownRef}>
            <NavButton
              variant="blue"
              arrowDirection="down"
              onClick={() => {
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className="w-full h-12 [&_span]:!text-base [&_span]:!leading-tight"
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
            >
              {LANGUAGE_LABELS[language]}
            </NavButton>

            {/* Language Dropdown */}
            {isDropdownOpen && (
              <div
                className="absolute top-full left-0 right-0 mt-2 bg-[#4A4A4A] border-[3px] border-black rounded-lg overflow-hidden z-10"
                role="listbox"
                aria-label="Select language"
              >
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      handleLanguageSelect(lang);
                    }}
                    className={`w-full px-4 py-3 text-white font-bold text-sm font-mind-boggle text-left hover:bg-[#5A5A5A] transition-colors ${
                      language === lang ? 'bg-[#5A5A5A]' : ''
                    }`}
                    style={createTextStroke('1.5px')}
                    role="option"
                    aria-selected={language === lang}
                  >
                    {LANGUAGE_LABELS[lang]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* ASSISTANCE Section */}
        <Section title={t('settings.assistance')}>
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <PillButton
                variant="blue"
                onClick={() => {
                  setShowRulesComingSoon(true);
                }}
                className="w-full h-12 [&_span]:!text-[0.85rem] [&_span]:!leading-tight"
              >
                {t('settings.rules')}
              </PillButton>
              <ComingSoon
                isOpen={showRulesComingSoon}
                onClose={() => {
                  setShowRulesComingSoon(false);
                }}
              />
            </div>
            <div className="relative flex-1">
              <PillButton
                variant="blue"
                onClick={() => {
                  setShowFaqComingSoon(true);
                }}
                className="w-full h-12 [&_span]:!text-[0.85rem] [&_span]:!leading-tight"
              >
                {t('settings.faq')}
              </PillButton>
              <ComingSoon
                isOpen={showFaqComingSoon}
                onClose={() => {
                  setShowFaqComingSoon(false);
                }}
              />
            </div>
          </div>
        </Section>

        {/* APPLICATION Section - only show if not installed and installable */}
        {!isInstalled && isInstallable && (
          <Section title={t('settings.application')}>
            <PillButton
              variant="blue"
              onClick={openDialog}
              className="w-full h-12 [&_span]:!text-[0.85rem] [&_span]:!leading-tight"
            >
              {t('settings.installApp')}
            </PillButton>
          </Section>
        )}

        {/* NOTIFICATIONS Section */}
        <Section title={t('settings.notifications')}>
          <div className="flex items-center justify-between gap-3">
            <span
              className="text-white font-bold text-sm font-mind-boggle"
              style={createTextStroke('1.5px')}
            >
              {t('settings.enableNotifications')}
            </span>
            <Toggle enabled={notificationsEnabled} onChange={setNotificationsEnabled} />
          </div>
        </Section>

        {/* DISCONNECT Button */}
        <div className="pt-2">
          <PillButton
            variant="red"
            onClick={onDisconnect}
            className="w-full h-12 [&_span]:!text-base [&_span]:!leading-tight"
          >
            {t('settings.disconnect')}
          </PillButton>
        </div>
      </div>
    </Dialog>
  );
}
